import isUnusual from 'utils/shared/trades/isUnusual';
import { CallPut } from 'utils/shared/trades/types';
import type { Trade } from 'utils/shared/trades/types';
import { getOptionFlowDirectionalSign } from 'utils/shared/trades/aggregateOptionFlow';
import { normalizeCallPut } from 'utils/shared/trades/normalizeCallPut';
import type { TradesByDate, TradesByExpiry, TradesByStrike } from './props';

function addTradeToGroup(
  group: TradesByDate | TradesByExpiry | TradesByStrike,
  trade: Trade,
): void {
  const size = trade.size || 0;
  const premium = trade.premium || 0;
  const tradeIsUnusual = isUnusual(trade);
  const directionalSign = getOptionFlowDirectionalSign(trade);
  const cp = normalizeCallPut(trade.cp);

  group.netVolume += size * directionalSign;
  group.netPremium += premium * directionalSign;

  if (directionalSign > 0) {
    group.bullishVolume = (group.bullishVolume || 0) + size;
    group.bullishPremium = (group.bullishPremium || 0) + premium;
  } else if (directionalSign < 0) {
    group.bearishVolume = (group.bearishVolume || 0) + size;
    group.bearishPremium = (group.bearishPremium || 0) + premium;
  } else {
    group.neutralVolume = (group.neutralVolume || 0) + size;
    group.neutralPremium = (group.neutralPremium || 0) + premium;
  }

  if (cp === CallPut.Call) {
    group.callVolume += size;
    group.callPremium += premium;

    if (tradeIsUnusual) {
      group.unusualCallVolume += size;
      group.unusualCallPremium += premium;
    }
  } else if (cp === CallPut.Put) {
    group.putVolume += size;
    group.putPremium += premium;

    if (tradeIsUnusual) {
      group.unusualPutVolume += size;
      group.unusualPutPremium += premium;
    }
  }
}

function createBaseGroup() {
  return {
    callVolume: 0,
    unusualCallVolume: 0,
    putVolume: 0,
    unusualPutVolume: 0,
    callPremium: 0,
    unusualCallPremium: 0,
    putPremium: 0,
    unusualPutPremium: 0,
    bullishVolume: 0,
    bearishVolume: 0,
    neutralVolume: 0,
    bullishPremium: 0,
    bearishPremium: 0,
    neutralPremium: 0,
    netVolume: 0,
    netPremium: 0,
  };
}

export function buildTradesByDate(trades: Trade[]): TradesByDate[] {
  const groupedTrades = trades.reduce((groups, trade) => {
    if (!groups[trade.date]) {
      groups[trade.date] = {
        date: trade.date,
        timestamp: new Date(trade.date).getTime(),
        ...createBaseGroup(),
      };
    }

    addTradeToGroup(groups[trade.date], trade);
    return groups;
  }, {} as Record<string, TradesByDate>);

  return Object.values(groupedTrades).sort((a, b) => b.timestamp - a.timestamp);
}

export function buildTradesByExpiry(trades: Trade[]): TradesByExpiry[] {
  const groupedTrades = trades.reduce((groups, trade) => {
    if (!groups[trade.expiryString]) {
      groups[trade.expiryString] = {
        expiry: trade.expiryString,
        timestamp: new Date(trade.expiryString).getTime(),
        ...createBaseGroup(),
      };
    }

    addTradeToGroup(groups[trade.expiryString], trade);
    return groups;
  }, {} as Record<string, TradesByExpiry>);

  return Object.values(groupedTrades).sort((a, b) => b.timestamp - a.timestamp);
}

export function buildTradesByStrike(trades: Trade[]): TradesByStrike[] {
  const groupedTrades = trades.reduce((groups, trade) => {
    const strike = trade.strike;
    if (!strike) return groups;

    const strikeKey = strike.toString();
    if (!groups[strikeKey]) {
      groups[strikeKey] = {
        strike,
        ...createBaseGroup(),
      };
    }

    addTradeToGroup(groups[strikeKey], trade);
    return groups;
  }, {} as Record<string, TradesByStrike>);

  return Object.values(groupedTrades).sort((a, b) => a.strike - b.strike);
}
